import { useEffect, useRef } from 'react';
import { useAds } from '../../hooks/useAds';
import { trackImpression, trackClick } from '../../services/adService';
import BannerAd from './BannerAd';
import NativeAd from './NativeAd';
import DisplayAd from './DisplayAd';
import SponsoredArticleAd from './SponsoredArticleAd';
import styled from 'styled-components';

const AdWrapper = styled.div`
  margin: ${props => props.margin || '1.5rem 0'};
  
  @media (min-width: 768px) {
    margin: ${props => props.margin || '2rem 0'};
  }
  
  @media (min-width: 1024px) {
    margin: ${props => props.margin || '2.5rem 0'};
  }
`;

const AdContainer = ({ 
  placement, 
  categoryId = null, 
  limit = 1,
  margin,
  className 
}) => {
  const { ads, loading } = useAds(placement, { categoryId, limit });
  const trackedImpressions = useRef(new Set());

  // Track impressions when ad becomes visible
  useEffect(() => {
    if (ads.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const adId = entry.target.dataset.adId;
            if (adId && !trackedImpressions.current.has(adId)) {
              trackImpression(adId);
              trackedImpressions.current.add(adId);
            }
          }
        });
      },
      { threshold: 0.5 } // Track when 50% visible
    );

    const elements = document.querySelectorAll(`[data-ad-id]`);
    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, [ads]);

  // Handle click tracking
  const handleClick = (adId, url) => {
    trackClick(adId);
    // Open in new tab
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return null; // Don't show loading state for ads
  }

  if (!ads || ads.length === 0) {
    return null; // Don't render empty ad containers
  }

  // Render first ad (or multiple if limit > 1)
  return (
    <AdWrapper margin={margin} className={className}>
      {ads.map((ad) => {
        const adProps = {
          key: ad._id,
          ad,
          onClick: () => handleClick(ad._id, ad.clickUrl),
        };

        switch (ad.type) {
          case 'banner':
            return <BannerAd {...adProps} />;
          case 'native':
            return <NativeAd {...adProps} />;
          case 'sponsored_article':
            return <SponsoredArticleAd {...adProps} />;
          case 'display':
            return <DisplayAd {...adProps} />;
          default:
            return <BannerAd {...adProps} />;
        }
      })}
    </AdWrapper>
  );
};

export default AdContainer;



